/**
 * React Query query hooks for data fetching
 *
 * These hooks provide caching, automatic refetching, and loading states
 * for read operations.
 */

import { toMinutes } from "@platform/api";
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { AxiosInstance } from "axios";

import {
  getRfpNote,
  listBenefitPlanGroups,
  loadCoverageByType,
  loadRfp,
} from "./client";
import { getCandidatePolicies } from "./clientBenefitPlanGroups";
import { listFieldComments } from "./clientFieldComments";
import { queryKeys } from "./queryKeys";
import { useApiClient } from "./useApiClient";

import type { FieldCommentResponse } from "./fieldCommentTypes";
import type {
  BenefitPlanGroup,
  CandidatePolicy,
  ListBenefitPlanGroupsParams,
  LoadCoverageParams,
  RfpCoverage,
  RfpData,
  RfpNotePayload,
} from "./types";

export { queryKeys } from "./queryKeys";

/**
 * Hook to load RFP data
 *
 * @param rfpId - RFP ID to load
 * @param axiosInstance - Optional axios instance (if not provided, uses context)
 * @param options - React Query options
 * @returns Query result with RFP data
 *
 * @example
 * ```tsx
 * // Using context (inside PlanDesignerProvider)
 * function RfpViewer({ rfpId }: { rfpId: string }) {
 *   const { data: rfp, isLoading, error } = useLoadRfp(rfpId);
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return <div>{rfp.name}</div>;
 * }
 *
 * // Using explicit axios instance (outside PlanDesignerProvider)
 * function RfpViewer({ rfpId }: { rfpId: string }) {
 *   const axiosInstance = useApi();
 *   const { data: rfp } = useLoadRfp(rfpId, axiosInstance);
 * }
 * ```
 */
export const useLoadRfp = (
  rfpId: string,
  axiosInstanceOrOptions?:
    | AxiosInstance
    | Omit<UseQueryOptions<RfpData, Error>, "queryKey" | "queryFn">,
  maybeOptions?: Omit<UseQueryOptions<RfpData, Error>, "queryKey" | "queryFn">,
): UseQueryResult<RfpData, Error> => {
  const contextAxiosInstance = useApiClient(true); // optional=true, won't throw outside provider

  // Determine if first optional param is axios instance or options
  let axiosInstance: AxiosInstance | undefined;
  let options:
    | Omit<UseQueryOptions<RfpData, Error>, "queryKey" | "queryFn">
    | undefined;

  if (axiosInstanceOrOptions && "get" in axiosInstanceOrOptions) {
    // First param is axios instance
    axiosInstance = axiosInstanceOrOptions as AxiosInstance;
    options = maybeOptions;
  } else {
    // First param is options (or undefined)
    axiosInstance = contextAxiosInstance;
    options = axiosInstanceOrOptions as
      | Omit<UseQueryOptions<RfpData, Error>, "queryKey" | "queryFn">
      | undefined;
  }

  if (!axiosInstance) {
    throw new Error(
      "No API client available for RFP loading. Either wrap component in PlanDesignerProvider with axiosInstance configured, or pass axiosInstance directly to useLoadRfp.",
    );
  }

  return useQuery({
    queryKey: queryKeys.rfp(rfpId),
    queryFn: () => loadRfp(rfpId, axiosInstance),
    staleTime: toMinutes(5),
    ...options,
  });
};

/**
 * Hook to load coverage by type
 *
 * @param params - Coverage load parameters
 * @param options - React Query options
 * @returns Query result with coverage data
 *
 * @example
 * ```tsx
 * function CoverageViewer() {
 *   const { data: coverage, isLoading } = useLoadCoverageByType({
 *     coverageType: "dental"
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *   return <CoverageGrid coverage={coverage} />;
 * }
 * ```
 */
export const useLoadCoverageByType = (
  params: LoadCoverageParams,
  options?: Omit<
    UseQueryOptions<RfpCoverage | null, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<RfpCoverage | null, Error> => {
  const axiosInstance = useApiClient();

  return useQuery({
    queryKey: queryKeys.coverage(params),
    queryFn: () => {
      if (!axiosInstance) {
        throw new Error("No API client available for loading coverage by type");
      }
      return loadCoverageByType(params, axiosInstance);
    },
    staleTime: toMinutes(5),
    enabled: !!params.coverageType,
    ...options,
  });
};

/**
 * Hook to list benefit plan groups
 *
 * @param params - Query parameters (rfpId, coverageType, productType)
 * @param options - React Query options
 * @returns Query result with benefit plan groups
 */
export const useListBenefitPlanGroups = (
  params: ListBenefitPlanGroupsParams,
  options?: Omit<
    UseQueryOptions<BenefitPlanGroup[], Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<BenefitPlanGroup[], Error> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("List Benefit Plan Groups API is not mocked");
  }

  return useQuery({
    queryKey: queryKeys.benefitPlanGroups(params),
    queryFn: () => listBenefitPlanGroups(params, axiosInstance),
    ...options,
  });
};

/**
 * Hook to fetch the candidate Current Carrier - Policy options for a coverage
 * (ADR 10.22). Like the other query hooks here, it requires the API client to
 * be configured (throws otherwise) so a missing client fails loudly rather
 * than silently showing an empty state.
 *
 * Takes the client from PlanDesignerProvider unless the caller passes one, as
 * useLoadRfp does, so a caller rendered outside the provider (the Quotes tab's
 * incumbent header) reads the same cached query as the policy picker.
 */
export const useGetCandidatePolicies = (
  coverageId: string,
  planType?: string,
  options?: Omit<
    UseQueryOptions<CandidatePolicy[], Error>,
    "queryKey" | "queryFn"
  >,
  explicitAxiosInstance?: AxiosInstance,
): UseQueryResult<CandidatePolicy[], Error> => {
  const contextAxiosInstance = useApiClient(true);
  const axiosInstance = explicitAxiosInstance ?? contextAxiosInstance;

  if (!axiosInstance) {
    throw new Error("Candidate Policies API is not mocked");
  }

  return useQuery({
    queryKey: queryKeys.candidatePolicies(coverageId, planType),
    queryFn: () => getCandidatePolicies(coverageId, planType, axiosInstance),
    enabled: Boolean(coverageId),
    // Candidate policies are coverage-scoped in-force broker policies that don't
    // change from rate edits. The in-grid current-policy header remounts on
    // benign re-renders (e.g. after an inline rate save), so without a staleTime
    // each remount refires this GET. Cache it so remounts serve from cache; a
    // coverage switch uses a different query key and still fetches fresh.
    staleTime: toMinutes(5),
    ...options,
  });
};

/**
 * Hook to fetch note for an RFP
 *
 * @param rfpId - RFP ID to fetch note for
 * @param options - React Query options
 * @returns Query result with note data
 *
 * @example
 * ```tsx
 * function RFPNoteDrawer({ rfpId }: { rfpId: string }) {
 *   const { data, isLoading } = useRfpNote(rfpId);
 *
 *   if (isLoading) return <Loading />;
 *
 *   return <Textarea value={data?.content ?? ""} />;
 * }
 * ```
 */
export const useRfpNote = (
  rfpId: string,
  options?: Omit<
    UseQueryOptions<RfpNotePayload, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<RfpNotePayload, Error> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("No API client available for RFP note");
  }

  return useQuery({
    queryKey: queryKeys.rfpNotes(rfpId),
    queryFn: () => getRfpNote(rfpId, axiosInstance),
    enabled: !!rfpId,
    ...options,
  });
};

export const useListFieldComments = (
  benefitPlanId: string,
  options?: Omit<
    UseQueryOptions<FieldCommentResponse[], Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<FieldCommentResponse[], Error> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("No API client available for listing field comments");
  }

  return useQuery({
    queryKey: queryKeys.fieldComments(benefitPlanId),
    queryFn: () => listFieldComments(benefitPlanId, axiosInstance),
    enabled: !!benefitPlanId,
    ...options,
  });
};

// Re-export queryKeys so existing consumers don't need to update imports
